import jsPDF from 'jspdf'
import 'jspdf-autotable'
import * as XLSX from 'xlsx'
import { formatRupees } from './currency'

/**
 * Export utilities for different file formats
 */

/**
 * Export data to CSV format
 * @param {Array} data - Array of expense objects
 * @param {string} filename - Name of the file (without extension)
 */
export const exportToCSV = (data, filename = 'expenses') => {
  const csvContent = [
    ['Title', 'Amount (₹)', 'Category', 'Description', 'Date'],
    ...data.map(expense => [
      expense.title,
      expense.amount,
      expense.category,
      expense.description || '',
      new Date(expense.created_at).toLocaleDateString('en-IN')
    ])
  ].map(row => row.map(field => `"${field}"`).join(',')).join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

/**
 * Export data to Excel format
 * @param {Array} data - Array of expense objects
 * @param {string} filename - Name of the file (without extension)
 */
export const exportToExcel = (data, filename = 'expenses') => {
  // Prepare data for Excel
  const excelData = data.map(expense => ({
    'Title': expense.title,
    'Amount (₹)': parseFloat(expense.amount),
    'Category': expense.category,
    'Description': expense.description || '',
    'Date': new Date(expense.created_at).toLocaleDateString('en-IN'),
    'Created At': new Date(expense.created_at).toLocaleString('en-IN')
  }))

  // Add summary row
  const totalAmount = data.reduce((sum, expense) => sum + parseFloat(expense.amount), 0)
  excelData.push({
    'Title': 'TOTAL',
    'Amount (₹)': totalAmount,
    'Category': '',
    'Description': `Total of ${data.length} expenses`,
    'Date': '',
    'Created At': ''
  })

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.json_to_sheet(excelData)

  // Auto-size columns
  const colWidths = [
    { wch: 25 }, // Title
    { wch: 15 }, // Amount
    { wch: 15 }, // Category
    { wch: 30 }, // Description
    { wch: 12 }, // Date
    { wch: 20 }  // Created At
  ]
  ws['!cols'] = colWidths

  // Style the total row
  const totalRowIndex = excelData.length
  const totalCellRange = XLSX.utils.encode_range({
    s: { c: 0, r: totalRowIndex },
    e: { c: 5, r: totalRowIndex }
  })

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Expenses')

  // Generate Excel file and download
  XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`)
}

/**
 * Export data to PDF format
 * @param {Array} data - Array of expense objects
 * @param {string} filename - Name of the file (without extension)
 */
export const exportToPDF = (data, filename = 'expenses') => {
  const doc = new jsPDF()
  
  // Add title
  doc.setFontSize(20)
  doc.setTextColor(40)
  doc.text('Expense Report', 20, 25)
  
  // Add metadata
  doc.setFontSize(12)
  doc.setTextColor(100)
  doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 20, 35)
  doc.text(`Total Expenses: ${data.length}`, 20, 42)
  
  // Calculate total amount
  const totalAmount = data.reduce((sum, expense) => sum + parseFloat(expense.amount), 0)
  doc.text(`Total Amount: ${formatRupees(totalAmount)}`, 20, 49)
  
  // Prepare table data
  const tableData = data.map(expense => [
    expense.title,
    formatRupees(expense.amount),
    expense.category,
    expense.description || '-',
    new Date(expense.created_at).toLocaleDateString('en-IN')
  ])
  
  // Add table
  doc.autoTable({
    head: [['Title', 'Amount', 'Category', 'Description', 'Date']],
    body: tableData,
    startY: 60,
    styles: {
      fontSize: 10,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [59, 130, 246], // Blue header
      textColor: 255,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252] // Light gray for alternate rows
    },
    columnStyles: {
      0: { cellWidth: 40 }, // Title
      1: { cellWidth: 25, halign: 'right' }, // Amount
      2: { cellWidth: 25 }, // Category
      3: { cellWidth: 45 }, // Description
      4: { cellWidth: 25 } // Date
    },
    margin: { top: 60, left: 20, right: 20 }
  })
  
  // Add footer with total
  const finalY = doc.lastAutoTable.finalY + 10
  doc.setFontSize(12)
  doc.setFont(undefined, 'bold')
  doc.text(`Total: ${formatRupees(totalAmount)}`, 20, finalY)
  
  // Add page numbers
  const pageCount = doc.internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(10)
    doc.setTextColor(150)
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() - 30,
      doc.internal.pageSize.getHeight() - 10
    )
  }
  
  // Save the PDF
  doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`)
}

/**
 * Export data with user's choice of format
 * @param {Array} data - Array of expense objects
 * @param {string} format - 'csv', 'excel', or 'pdf'
 * @param {string} filename - Name of the file (without extension)
 */
export const exportData = (data, format, filename = 'expenses') => {
  switch (format.toLowerCase()) {
    case 'csv':
      exportToCSV(data, filename)
      break
    case 'excel':
    case 'xlsx':
      exportToExcel(data, filename)
      break
    case 'pdf':
      exportToPDF(data, filename)
      break
    default:
      throw new Error(`Unsupported export format: ${format}`)
  }
}

export default {
  exportToCSV,
  exportToExcel,
  exportToPDF,
  exportData
}
